import { useState, useMemo, useEffect, useCallback } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ArrowLeft, Printer, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  X,
  Building2,
  AlertCircle,
} from 'lucide-react';
import { useCompany } from '@/hooks/use-company';
import { useBranch } from '@/hooks/use-branch';
import {
  getAttendanceReport,
  getDateRangeFromPeriod,
  type AttendanceReportPeriod,
  type AttendanceReportData,
  type AttendanceReportSummary,
} from '@/services/reports/attendance';
import type { EmployeeListEmployee } from '@/services/employee';
import {
  AttendanceReportPageHeader,
  FilterControls,
  AttendanceReportTabbedContent,
  AttendanceReportPrintView,
  type AttendanceStatus,
} from '@/components/company/reports/attendance';

interface AttendanceLogEntry {
  name: string;
  officeTime?: string;
  workingHrs?: string;
  fullPresent: number;
  halfPresent: number;
  late: number;
  absent: number;
  wfh: number;
  leaves: number;
  avgHrs?: string;
  totalHrs?: string;
  ot?: string;
  offDays: number;
  [key: string]: unknown;
}

const searchSchema = z.object({
  period: z.string().optional().catch('this-month'),
});

type SearchSchema = z.infer<typeof searchSchema>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = createFileRoute('/(app)/company/reports/attendance/' as any)({
  component: AttendanceReportsPage,
  validateSearch: searchSchema,
});

function AttendanceReportsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { selectedCompanyId } = useCompany();
  const { selectedBranchId: globalBranchId } = useBranch(selectedCompanyId);
  
  // Use global branch selection, but only if it's a valid branch (> 0, not -1 for "All Branches")
  // Sync with sidebar branch filter selection
  const selectedBranchId = useMemo(() => {
    return globalBranchId && globalBranchId > 0 ? globalBranchId : undefined;
  }, [globalBranchId]);

  // Tab state
  const [activeTab, setActiveTab] = useState<'analytics' | 'data'>('analytics');

  // Pagination state
  const [logsPage, setLogsPage] = useState(1);

  // Print state
  const [isPrinting, setIsPrinting] = useState(false);

  // Filter state
  const [period, setPeriod] = useState<AttendanceReportPeriod>(
    ((search as SearchSchema).period as AttendanceReportPeriod) || 'this-month'
  );
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListEmployee | null>(null);
  const [filterStatuses, setFilterStatuses] = useState<AttendanceStatus[]>([]);

  // Check if valid branch is selected (must be > 0, not -1 for "All Branches")
  const isValidBranch = selectedBranchId !== undefined && selectedBranchId !== null && selectedBranchId > 0;

  // Build base request params - only when company and valid branch are selected
  const baseParams = useMemo(() => {
    if (!selectedCompanyId || !selectedBranchId || selectedBranchId <= 0) return null;

    const params: {
      company_id: number;
      branch_id: number;
      employee_id: number | string;
      period?: AttendanceReportPeriod;
    } = {
      company_id: selectedCompanyId,
      branch_id: selectedBranchId,
      employee_id: selectedEmployee ? selectedEmployee.id : '0', // Use "0" string for branch rollup to match API
      period,
    };

    return params;
  }, [
    selectedCompanyId,
    selectedBranchId,
    period,
    selectedEmployee,
  ]);

  // Fetch attendance report data - only when valid branch is selected
  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['attendance-report', baseParams],
    queryFn: () => getAttendanceReport(baseParams!),
    enabled: !!baseParams,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  const reportData: AttendanceReportData | undefined = response?.data;

  // Ensure data has start_date/end_date from selected period when API does not return them
  const reportDataWithDates = useMemo(() => {
    if (!reportData) return undefined;
    const range = getDateRangeFromPeriod(period);
    return {
      ...reportData,
      start_date: reportData.start_date ?? range?.start_date,
      end_date: reportData.end_date ?? range?.end_date,
      period: reportData.period ?? period,
    };
  }, [reportData, period]);

  // Calculate pagination for attendance logs (client-side pagination)
  const ITEMS_PER_PAGE = 20;
  
  // Helper function to check if an employee log matches selected statuses
  const matchesFilterStatuses = (log: AttendanceLogEntry, statuses: AttendanceStatus[], redFlagNames: Set<string>) => {
    if (statuses.length === 0) return true;

    return statuses.some((status) => {
      switch (status) {
        case 'PRESENT':
          return (log.fullPresent || 0) > 0;
        case 'ABSENT':
          return (log.absent || 0) > 0;
        case 'LATE':
          return (log.late || 0) > 0;
        case 'ON_LEAVE':
          return (log.leaves || 0) > 0;
        case 'HALF_DAY':
          return (log.halfPresent || 0) > 0;
        case 'WFH':
          return (log.wfh || 0) > 0;
        case 'WEEKLY_OFF':
          return (log.offDays || 0) > 0;
        case 'HOLIDAY':
          // Holiday is not directly tracked per employee in logs
          return false;
        case 'RED_FLAG': {
          // Check if employee name exists in redFlags array
          const logName = (String(log.name || '')).toUpperCase().trim();
          return redFlagNames.has(logName);
        }
        default:
          return false;
      }
    });
  };

  // Filter attendance logs based on selected statuses (for both Analytics and Data tabs)
  const filteredAttendanceLogs = useMemo(() => {
    // Early return if no report data exists
    if (!reportData?.report) {
      return [];
    }
    
    // Handle employee reports differently from branch reports
    if (reportData.report_type === 'employee') {
      // Employee report: data is in report.data.attendanceLog (day-wise records)
      const reportObj = reportData.report as Record<string, unknown>;
      const reportDataObj = reportObj.data as Record<string, unknown> | undefined;
      const dayWiseLogs = reportDataObj?.attendanceLog;

      // If no day-wise data, return empty
      if (!dayWiseLogs || !Array.isArray(dayWiseLogs) || dayWiseLogs.length === 0) {
        return [];
      }

      // Get employee info
      const reportEmployee = reportObj.employee as Record<string, unknown> | undefined;
      const employee = reportData.employee || reportEmployee;
      const employeeName = (employee?.name as string) || 'Employee';
      const officeTime = (employee?.officeTime as string) || (reportEmployee?.officeTime as string) || (reportDataObj?.officeTime as string) || '-';
      
      // Aggregate day-wise records into summary counts
      let fullPresent = 0;
      let halfPresent = 0;
      let late = 0;
      let absent = 0;
      let wfh = 0;
      let leaves = 0;
      let offDays = 0;
      let totalWorkHours = 0;
      let totalOT = 0;
      let daysWithWorkHours = 0;
      
      dayWiseLogs.forEach((day: Record<string, unknown>) => {
        const status = (String(day.status || '')).toLowerCase().trim();
        
        // Count status occurrences
        if (status.includes('present') && !status.includes('half')) {
          fullPresent++;
        } else if (status.includes('half')) {
          halfPresent++;
        } else if (status.includes('absent')) {
          absent++;
        } else if (status.includes('leave') || status.includes('on leave')) {
          leaves++;
        } else if (status.includes('week off') || status.includes('weekly off')) {
          offDays++;
        } else if (status.includes('wfh') || status.includes('work from home')) {
          wfh++;
        }

        // Count late occurrences (e.g., "late", "present (late)")
        if (status.includes('late')) {
          late++;
        }
        
        // Count late occurrences (e.g., "late", "present (late)")
        // This is a separate check because "present (late)" should count as both present AND late
        if (status.includes('late')) {
          late++;
        }
        
        // Parse work hours if available
        if (day.workHours && day.workHours !== '-') {
          // Try to parse hours from format like "8h 30m" or "8.5" or "8:30"
          const workHoursStr = String(day.workHours).trim();
          let hours = 0;
          
          // Try parsing "Xh Ym" format
          const hourMatch = workHoursStr.match(/(\d+(?:\.\d+)?)\s*h/i);
          const minuteMatch = workHoursStr.match(/(\d+)\s*m/i);
          
          if (hourMatch) {
            hours += parseFloat(hourMatch[1]);
          }
          if (minuteMatch) {
            hours += parseFloat(minuteMatch[1]) / 60;
          }
          
          // If no match, try parsing "HH:MM" or "H:MM" format (e.g., "8:30" or "08:30")
          if (!hourMatch && !minuteMatch) {
            const timeMatch = workHoursStr.match(/^(\d{1,2}):(\d{2})$/);
            if (timeMatch) {
              const h = parseFloat(timeMatch[1]) || 0;
              const m = parseFloat(timeMatch[2]) || 0;
              hours = h + (m / 60);
            } else {
              // Fallback to direct parse (for formats like "8.5")
              hours = parseFloat(workHoursStr) || 0;
            }
          }
          
          if (hours > 0) {
            totalWorkHours += hours;
            daysWithWorkHours++;
          }
        }
        
        // Parse OT hours
        if (day.otHours && day.otHours !== '-' && day.otHours !== '0') {
          const otStr = String(day.otHours).trim();
          const ot = parseFloat(otStr) || 0;
          if (ot > 0) {
            totalOT += ot;
          }
        }
      });
      
      // Create a summary row from aggregated data
      const summaryRow = {
        name: employeeName,
        date: reportData.start_date || '', // Use period start date
        officeTime: officeTime,
        workingHrs: totalWorkHours > 0 ? `${totalWorkHours.toFixed(2)}h` : '-',
        fullPresent,
        halfPresent,
        late,
        absent,
        wfh,
        leaves,
        avgHrs: daysWithWorkHours > 0 
          ? `${(totalWorkHours / daysWithWorkHours).toFixed(2)}h` 
          : dayWiseLogs.length > 0 
            ? `${(totalWorkHours / dayWiseLogs.length).toFixed(2)}h` 
            : '0h',
        totalHrs: totalWorkHours > 0 ? `${totalWorkHours.toFixed(2)}h` : '0h',
        ot: totalOT > 0 ? `${totalOT.toFixed(2)}h` : '0h',
        offDays,
      };
      
      // Apply status filters if any are selected
      if (filterStatuses.length > 0) {
        const redFlagNames = new Set(
          (reportData.report.redFlags || []).map((flag) => (String(flag.name || '')).toUpperCase().trim())
        );
        
        const matches = matchesFilterStatuses(summaryRow, filterStatuses, redFlagNames);
        return matches ? [summaryRow] : [];
      }
      
      return [summaryRow];
    } else {
      // Branch report: use report.attendanceLog directly
      const attendanceLog = reportData.report.attendanceLog;
      
      // If attendanceLog doesn't exist or is not an array, return empty array
      if (!attendanceLog || !Array.isArray(attendanceLog)) {
        return [];
      }
      
      // If attendanceLog is empty, return empty array (no data to filter)
      if (attendanceLog.length === 0) {
        return [];
      }
      
      // Create a copy for filtering
      let logs = [...attendanceLog];
      
      // Get red flags set for quick lookup
      const redFlagNames = new Set(
        (reportData.report.redFlags || []).map((flag) => (String(flag.name || '')).toUpperCase().trim())
      );
      
      // Apply status filters if any are selected
      if (filterStatuses.length > 0) {
        logs = logs.filter((log) => matchesFilterStatuses(log as AttendanceLogEntry, filterStatuses, redFlagNames));
      }

      return logs;
    }
  }, [reportData, filterStatuses]);

  // Create filtered report data for Analytics tab (includes recalculated summary stats)
  const filteredReportData = useMemo(() => {
    if (!reportData?.report) return reportData;
    
    // Get filtered logs
    const filteredLogs = filteredAttendanceLogs;
    
    // Filter red flags - only include employees that match the filter statuses
    const filteredRedFlags = filterStatuses.length > 0 && filterStatuses.includes('RED_FLAG')
      ? (reportData.report.redFlags || []).filter((flag) => {
          const flagName = (String(flag.name || '')).toUpperCase().trim();
          // Include if employee is in filtered logs (they match other statuses) OR if only RED_FLAG is selected
          return filteredLogs.some((log) => (String(log.name || '')).toUpperCase().trim() === flagName) ||
                 (filterStatuses.length === 1 && filterStatuses[0] === 'RED_FLAG');
        })
      : filterStatuses.length > 0
        ? (reportData.report.redFlags || []).filter((flag) => {
            // Only include red flags if the employee matches other selected statuses
            const flagName = (String(flag.name || '')).toUpperCase().trim();
            return filteredLogs.some((log) => (String(log.name || '')).toUpperCase().trim() === flagName);
          })
        : (reportData.report.redFlags || []);

    // Filter departments - recalculate based on filtered logs
    const filteredDepartments = (reportData.report.departments || []).map((dept) => {
      // Count employees in filtered logs for this department
      // Note: This is an approximation since department info isn't in attendanceLog
      // We'll use the original department data but this could be enhanced with department mapping
      return dept;
    });
    
    // Recalculate summary statistics from filtered logs
    const recalculatedSummary = {
      ...reportData.report,
      attendanceLog: filteredLogs,
      redFlags: filteredRedFlags,
      departments: filteredDepartments,
      // Recalculate counts from filtered logs
      totalEmployees: filteredLogs.length,
      present: filteredLogs.reduce((sum, log) => sum + (log.fullPresent || 0), 0),
      absent: filteredLogs.reduce((sum, log) => sum + (log.absent || 0), 0),
      late: filteredLogs.reduce((sum, log) => sum + (log.late || 0), 0),
      onLeave: filteredLogs.reduce((sum, log) => sum + (log.leaves || 0), 0),
      halfDay: filteredLogs.reduce((sum, log) => sum + (log.halfPresent || 0), 0),
      wfhCount: filteredLogs.reduce((sum, log) => sum + (log.wfh || 0), 0),
      // Keep original values for fields that can't be recalculated from logs
      weekoff: reportData.report.weekoff,
      holiday: reportData.report.holiday,
      unauthorizedCount: reportData.report.unauthorizedCount,
      // Recalculate percentages if possible
      onTimePercentage: filteredLogs.length > 0 
        ? ((filteredLogs.reduce((sum, log) => sum + (log.fullPresent || 0), 0) / filteredLogs.length) * 100) 
        : reportData.report.onTimePercentage,
      latePercentage: filteredLogs.length > 0
        ? ((filteredLogs.reduce((sum, log) => sum + (log.late || 0), 0) / filteredLogs.length) * 100)
        : reportData.report.latePercentage,
      halfDayPercentage: filteredLogs.length > 0
        ? ((filteredLogs.reduce((sum, log) => sum + (log.halfPresent || 0), 0) / filteredLogs.length) * 100)
        : reportData.report.halfDayPercentage,
    };
    
    return {
      ...reportData,
      report: recalculatedSummary,
    };
  }, [reportData, filteredAttendanceLogs, filterStatuses]);
  
  // Create modified report data with paginated and filtered logs
  const paginatedReportData = useMemo(() => {
    if (!reportData) return undefined; // Return undefined if no data
    
    const totalLogs = filteredAttendanceLogs.length;
    const totalPages = Math.ceil(totalLogs / ITEMS_PER_PAGE);
    const startIndex = (logsPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedLogs = filteredAttendanceLogs.slice(startIndex, endIndex);

    // Pagination object similar to approvals report structure
    const logsPagination = totalPages > 0 ? {
      total: totalLogs,
      per_page: ITEMS_PER_PAGE,
      current_page: logsPage,
      last_page: totalPages,
      total_pages: totalPages,
      limit: ITEMS_PER_PAGE,
    } : undefined;
    
    return {
      ...reportData,
      report: reportData.report ? {
        ...reportData.report,
        attendanceLog: paginatedLogs, // Use filtered and paginated logs
      } : reportData.report,
      logsPagination,
    };
  }, [reportData, filteredAttendanceLogs, logsPage]);

  // Reset page if out of bounds after filtering
  useEffect(() => {
    const totalLogs = filteredAttendanceLogs.length;
    const totalPages = Math.ceil(totalLogs / ITEMS_PER_PAGE);
    if (totalPages > 0 && logsPage > totalPages) {
      setLogsPage(1);
    } else if (totalPages === 0 && logsPage > 1) {
      setLogsPage(1);
    }
  }, [filteredAttendanceLogs, logsPage]);

  // Handlers
  const handlePeriodChange = (value: AttendanceReportPeriod) => {
    setPeriod(value);
    setLogsPage(1); // Reset to first page when filter changes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate({ search: { period: value } as any, replace: true });
  };

  const handleEmployeeChange = (employee: EmployeeListEmployee | null) => {
    setSelectedEmployee(employee);
    setLogsPage(1); // Reset to first page when filter changes
  };

  const handleStatusesChange = (statuses: AttendanceStatus[]) => {
    setFilterStatuses(statuses);
    setLogsPage(1); // Reset to first page when filter changes
  };

  // Print: reset state after print dialog closes
  useEffect(() => {
    const handleAfterPrint = () => {
      setIsPrinting(false);
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, []);

  // Trigger print (attendance data is already fully loaded, no pagination to fetch)
  const handlePrint = useCallback(() => {
    if (!reportData) return;
    setIsPrinting(true);

    // Wait for the print view (including charts) to fully render and lay out
    requestAnimationFrame(() => {
      setTimeout(() => {
        window.print();
      }, 800);
    });
  }, [reportData]);

  // Check if no company selected
  if (!selectedCompanyId) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="flex items-center justify-between gap-4">
          <Button asChild variant="outline" size="sm">
            <Link to="/company/reports">
              <ArrowLeft className="mr-2 size-4" />
              Back to Reports
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <Building2 className="mx-auto size-12 text-muted-foreground/50" />
              <p className="mt-4 text-lg font-medium">No Company Selected</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Please select a company to view the attendance report
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if no valid branch selected - show "Please Select a Branch" message
  if (!isValidBranch) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="flex items-center justify-between gap-4">
          <Button asChild variant="outline" size="sm">
            <Link to="/company/reports">
              <ArrowLeft className="mr-2 size-4" />
              Back to Reports
            </Link>
          </Button>
        </div>

        {/* Filter Controls */}
        <div className="flex justify-end gap-2">
          <FilterControls
            period={period}
            selectedEmployee={selectedEmployee}
            selectedBranchId={selectedBranchId}
            filterStatuses={filterStatuses}
            onPeriodChange={handlePeriodChange}
            onEmployeeChange={handleEmployeeChange}
            onStatusesChange={handleStatusesChange}
          />
        </div>

        {/* "Please Select a Branch" Message */}
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <AlertCircle className="mx-auto size-12 text-yellow-500/50" />
              <p className="mt-4 text-lg font-medium">Please Select a Branch</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose a branch from the filter above to view the attendance report
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check for error state
  if (isError) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="flex items-center justify-between gap-4">
          <Button asChild variant="outline" size="sm">
            <Link to="/company/reports">
              <ArrowLeft className="mr-2 size-4" />
              Back to Reports
            </Link>
          </Button>
        </div>
        <AttendanceReportPageHeader summary={undefined} />
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <X className="mx-auto size-12 text-destructive" />
              <p className="mt-4 text-lg font-medium">Failed to load report</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {error instanceof Error ? error.message : 'An error occurred'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button + Print Button */}
      <div className="flex items-center justify-between gap-4 no-print">
        <Button asChild variant="outline" size="sm">
          <Link to="/company/reports">
            <ArrowLeft className="mr-2 size-4" />
            Back to Reports
          </Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrint}
          disabled={isPrinting || !reportData}
        >
          {isPrinting ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Printer className="mr-2 size-4" />
          )}
          {isPrinting ? 'Preparing...' : 'Print Report'}
        </Button>
      </div>

      {/* Header with Stats - Use filtered data when filters are applied; pass data for working-days metrics */}
      <div className="no-print">
        <AttendanceReportPageHeader
          summary={filterStatuses.length > 0 ? filteredReportData?.report as AttendanceReportSummary : reportData?.report}
          data={
            filterStatuses.length > 0
              ? (filteredReportData
                  ? { ...filteredReportData, start_date: reportDataWithDates?.start_date, end_date: reportDataWithDates?.end_date } as AttendanceReportData
                  : undefined)
              : reportDataWithDates
          }
        />
      </div>

      {/* Tab Toggle and Filter Controls Row */}
      <div className="no-print">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'analytics' | 'data')}>
          <div className="flex items-center justify-between gap-4">
            {/* Tab Toggle (Left) */}
            <TabsList>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="data">Data</TabsTrigger>
            </TabsList>

            {/* Filter Controls (Right) */}
            <FilterControls
              period={period}
              selectedEmployee={selectedEmployee}
              selectedBranchId={selectedBranchId}
              filterStatuses={filterStatuses}
              onPeriodChange={handlePeriodChange}
              onEmployeeChange={handleEmployeeChange}
              onStatusesChange={handleStatusesChange}
            />
          </div>

          {/* Tabbed Content */}
          <div className="mt-6">
            <AttendanceReportTabbedContent
              data={activeTab === 'analytics'
                ? (filterStatuses.length > 0
                    ? (filteredReportData
                        ? { ...filteredReportData, start_date: reportDataWithDates?.start_date, end_date: reportDataWithDates?.end_date, period: reportDataWithDates?.period } as AttendanceReportData
                        : undefined)
                    : reportDataWithDates)
                : (paginatedReportData
                    ? {
                        ...paginatedReportData,
                        start_date: reportDataWithDates?.start_date,
                        end_date: reportDataWithDates?.end_date,
                        period: reportDataWithDates?.period,
                      } as AttendanceReportData
                    : undefined)
              }
              isLoading={isLoading}
              activeTab={activeTab}
              currentPage={logsPage}
              onPageChange={setLogsPage}
            />
          </div>
        </Tabs>
      </div>

      {/* Print View - hidden on screen, visible only when printing */}
      {isPrinting && reportData && (
        <AttendanceReportPrintView data={reportData} />
      )}
    </div>
  );
}
